import { z } from "zod";

/**
 * Boolean query-string parser. `z.coerce.boolean()` is wrong for URL params
 * because `Boolean("false") === true` — any non-empty string is truthy in JS.
 * Accept the canonical string literals plus a real boolean (server-side call
 * sites sometimes pass booleans directly). Exported as a function to avoid
 * module-scope TDZ / HMR hoist quirks.
 */
function queryBool() {
  return z
    .union([z.boolean(), z.string()])
    .transform((v) => {
      if (typeof v === "boolean") return v;
      const s = v.toLowerCase();
      return s === "true" || s === "1";
    });
}

/**
 * DTO schemas shared between `apps/api` and `apps/web`.
 *
 * M3 (Auth) ships LoginDto + MeResponse. M5 (Wallets) adds the Wallet CRUD
 * surface below. AnnotationDto remains a placeholder filled at M11.
 */

/**
 * Login request body. Email is normalized (lower-cased + trimmed) at the schema
 * boundary so every downstream lookup uses the canonical form.
 */
export const LoginDto = z
  .object({
    email: z
      .string()
      .email()
      .max(254)
      .transform((s) => s.toLowerCase().trim()),
    password: z.string().min(1).max(200),
  })
  .strict();
export type LoginDto = z.infer<typeof LoginDto>;

/**
 * Display-currency for the USD/EUR Amount column in tx tables. Persisted
 * per user (see `User.preferredCurrency` in the Prisma schema). v1.1 may add
 * more codes; the API + web clamp to this union for now.
 */
export const PreferredCurrencySchema = z.enum(["USD", "EUR"]);
export type PreferredCurrency = z.infer<typeof PreferredCurrencySchema>;

/* -------------------------------------------------------------------------- */
/*  Multi-tenant Accounts — Zod DTOs                                          */
/* -------------------------------------------------------------------------- */

/**
 * Per-account role. Upper-case to match the Prisma enum directly so the wire
 * shape and DB shape are the same thing.
 */
export const AccountRoleSchema = z.enum(["ADMIN", "MANAGER", "VIEWER"]);
export type AccountRole = z.infer<typeof AccountRoleSchema>;

/**
 * Password policy: at least 8 chars, at least one lower, one upper, one digit.
 * Exported as both a raw regex (for non-Zod call sites) and a Zod schema that
 * carries the official validation message. Re-used by register,
 * change-password, and admin add-member flows.
 */
export const PasswordPolicyRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
export const PasswordPolicySchema = z
  .string()
  .regex(
    PasswordPolicyRegex,
    "Password must be at least 8 chars with upper, lower, and a digit",
  );

export const AccountDto = z
  .object({
    id: z.string(),
    name: z.string(),
    createdAt: z.string(),
    /**
     * ISO-8601 soft-delete marker. Present and non-null only when the super
     * admin has soft-deleted the account. Optional so tenant responses (which
     * never expose tombstoned accounts) can omit the field without tripping
     * strict parse. The super-admin list endpoint sets `null` for live rows
     * and the tombstone date for disabled rows so the UI can badge them.
     */
    deletedAt: z.string().nullable().optional(),
  })
  .strict();
export type AccountDto = z.infer<typeof AccountDto>;

/**
 * Body for `POST /_superadmin/accounts`. Either `ownerUserId` (already
 * resolved server-side) or `ownerEmail` (resolved by the service via the
 * User table) installs the first ADMIN. When both are omitted the calling
 * super admin is used.
 */
export const AccountCreateDto = z
  .object({
    name: z.string().trim().min(1).max(120),
    ownerUserId: z.string().min(1).optional(),
    ownerEmail: z
      .string()
      .email()
      .max(254)
      .transform((s) => s.toLowerCase().trim())
      .optional(),
  })
  .strict();
export type AccountCreateDto = z.infer<typeof AccountCreateDto>;

/**
 * Body for super-admin + account-admin password resets. Re-uses
 * {@link PasswordPolicySchema} so the UI and the service enforce the same
 * floor.
 */
export const AdminResetPasswordDto = z
  .object({
    newPassword: PasswordPolicySchema,
  })
  .strict();
export type AdminResetPasswordDto = z.infer<typeof AdminResetPasswordDto>;

/**
 * Query parameters for `GET /_superadmin/users`. Every filter is optional;
 * `page` / `pageSize` default to a 50-row page at page 1. `accountId` filters
 * to "is a member of this account", `active` filters by `User.active`, `q`
 * is a case-insensitive email contains match.
 */
export const SuperAdminUserListQuery = z.object({
  q: z.string().trim().max(120).optional(),
  accountId: z.string().min(1).max(64).optional(),
  active: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type SuperAdminUserListQuery = z.infer<typeof SuperAdminUserListQuery>;

export const SuperAdminUserListItemDto = z.object({
  id: z.string(),
  email: z.string(),
  isSuperAdmin: z.boolean(),
  active: z.boolean(),
  mustChangePassword: z.boolean(),
  createdAt: z.string(),
  memberships: z.array(
    z.object({
      accountId: z.string(),
      accountName: z.string(),
      role: AccountRoleSchema,
    }),
  ),
});
export type SuperAdminUserListItemDto = z.infer<typeof SuperAdminUserListItemDto>;

export const SuperAdminUserListResponse = z.object({
  items: z.array(SuperAdminUserListItemDto),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type SuperAdminUserListResponse = z.infer<typeof SuperAdminUserListResponse>;

export const MembershipDto = z
  .object({
    accountId: z.string(),
    accountName: z.string(),
    userId: z.string(),
    email: z.string().email(),
    role: AccountRoleSchema,
  })
  .strict();
export type MembershipDto = z.infer<typeof MembershipDto>;

/**
 * Envelope returned by `GET /accounts/:id/members`. The `{items: [...]}`
 * wrapper matches `GET /accounts/me` and `GET /_superadmin/accounts` so the
 * web client uses the same unwrap helper on every list endpoint.
 */
export const MembershipListResponse = z
  .object({
    items: z.array(MembershipDto),
  })
  .strict();
export type MembershipListResponse = z.infer<typeof MembershipListResponse>;

export const RegisterDto = z
  .object({
    email: z
      .string()
      .email()
      .max(254)
      .transform((s) => s.toLowerCase().trim()),
    password: PasswordPolicySchema,
  })
  .strict();
export type RegisterDto = z.infer<typeof RegisterDto>;

export const AddMemberDto = z
  .object({
    email: z
      .string()
      .email()
      .max(254)
      .transform((s) => s.toLowerCase().trim()),
    tempPassword: PasswordPolicySchema,
    role: AccountRoleSchema,
  })
  .strict();
export type AddMemberDto = z.infer<typeof AddMemberDto>;

export const ChangePasswordDto = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: PasswordPolicySchema,
  })
  .strict();
export type ChangePasswordDto = z.infer<typeof ChangePasswordDto>;

/**
 * Body for `POST /auth/forgot-password`. Server normalises the email
 * (lower-case + trim) at the schema boundary. The response is always
 * `204` regardless of whether the email matches an account (anti-
 * enumeration) — see `passwordResetService` for the orchestration.
 */
export const ForgotPasswordDto = z
  .object({
    email: z
      .string()
      .email()
      .max(254)
      .transform((s) => s.toLowerCase().trim()),
  })
  .strict();
export type ForgotPasswordDto = z.infer<typeof ForgotPasswordDto>;

/**
 * Body for `POST /auth/reset-password`. The plaintext token is the URL
 * path segment from the email; the new password must meet the platform
 * policy. Bounded length on `token` so a tampered payload can't smuggle
 * a giant string into the consume path.
 */
export const ResetPasswordDto = z
  .object({
    token: z.string().min(1).max(256),
    newPassword: PasswordPolicySchema,
  })
  .strict();
export type ResetPasswordDto = z.infer<typeof ResetPasswordDto>;

/**
 * Switch-account modes — super-admin only:
 *   - `"global"` (default): clears any active `viewAsAccountId` JWT claim so
 *     a super admin sees their cross-tenant aggregate view. Always the
 *     correct mode for tenant ADMIN/MANAGER/VIEWER callers (they have no
 *     view-as concept; the route 400s if they try to pass anything else).
 *   - `"viewAs"`: super admin only. Sets the new JWT's `viewAsAccountId`
 *     claim so subsequent requests are scoped to `accountId` exactly as
 *     that account's ADMIN would see them (see `accountScope.ts`).
 */
export const SwitchAccountModeSchema = z.enum(["global", "viewAs"]);

export const SwitchAccountDto = z
  .object({
    accountId: z.string().min(1),
    /**
     * Defaults to `"global"` so existing tenant + super-admin callers that
     * don't know about view-as keep their current behaviour.
     */
    mode: SwitchAccountModeSchema.optional(),
  })
  .strict();
export type SwitchAccountDto = z.infer<typeof SwitchAccountDto>;

/**
 * Shape returned by `GET /auth/me` — also consumed by the web auth context.
 *
 * Multi-tenant: the legacy `role` / `orgScope` fields are dropped. The web
 * consumes `memberships` to render the account switcher and derives the
 * effective role from `memberships.find(m => m.accountId === activeAccountId)`.
 */
export const MeResponse = z.object({
  id: z.string(),
  email: z.string().email(),
  memberships: z.array(MembershipDto),
  activeAccountId: z.string().nullable(),
  isSuperAdmin: z.boolean(),
  mustChangePassword: z.boolean(),
  /**
   * M14: default currency for the USD/EUR amount column. Optional here so
   * tests/older fixtures that pre-date M14 still parse — the web side reads
   * it with a `?? "USD"` fallback.
   */
  preferredCurrency: PreferredCurrencySchema.optional(),
  /**
   * Super-admin "view as account" mode. When non-null the caller is a super
   * admin who has picked a tenant account to impersonate; their request
   * scope flips from cross-tenant to that account exactly as that account's
   * ADMIN would see it. `viewAsAccountName` is joined from `Account.name`
   * so the web banner can render without a second round-trip.
   *
   * Always null for tenant callers — the server rejects any tenant attempt
   * to set the underlying JWT claim.
   */
  viewAsAccountId: z.string().nullable().optional(),
  viewAsAccountName: z.string().nullable().optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

/**
 * Body for `PATCH /users/me` — self-service preferences update. Strict so
 * stray fields cause a clean 400 at the route boundary.
 */
export const UserSelfPatchDto = z
  .object({
    preferredCurrency: PreferredCurrencySchema,
  })
  .strict();
export type UserSelfPatchDto = z.infer<typeof UserSelfPatchDto>;

/* -------------------------------------------------------------------------- */
/*  M5 — Wallets                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Chain identifiers are ALL-CAPS / digit / `_` / `-` tokens (e.g. "BTC",
 * "ETH", "TRX"). Kept loose here because new chains are seeded in the DB
 * without a code change. Address format is validated server-side via the
 * per-chain adapter; this is only the shape guard.
 */
export const ChainIdSchema = z.string().min(1).max(32).regex(/^[A-Z0-9_-]+$/);

export const WalletTokenNameSchema = z.string().trim().min(1).max(80);

/**
 * Raw address shape — must be non-empty and within a generous bound. Chain-
 * specific regex + normalization happens server-side in `validateAddressForChain`.
 */
export const AddressSchema = z.string().trim().min(4).max(128);

export const WalletTokenCreateDto = z
  .object({
    name: WalletTokenNameSchema,
    chainId: ChainIdSchema,
    address: AddressSchema,
    /**
     * H2a — optional Wallet (group) id. `null`/omitted keeps the token in
     * the "Ungrouped" bucket; a string id moves the token into that group.
     * The server validates the group belongs to the caller's account.
     */
    walletId: z.string().min(1).nullable().optional(),
  })
  .strict();
export type WalletTokenCreateDto = z.infer<typeof WalletTokenCreateDto>;

export const WalletTokenPatchDto = z
  .object({
    name: WalletTokenNameSchema.optional(),
    /**
     * H2a — move the token between groups. `null` explicitly ungroups;
     * `undefined` leaves the existing value untouched.
     */
    walletId: z.string().min(1).nullable().optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.walletId !== undefined, {
    message: "Provide name or walletId.",
  });
export type WalletTokenPatchDto = z.infer<typeof WalletTokenPatchDto>;

/**
 * Wallet lifecycle filter. Defaults to `active` so the common list endpoint
 * never surfaces disabled (soft-deleted) wallets without an explicit opt-in.
 * `all` is admin-only visibility; the service layer enforces the role check.
 */
export const WalletTokenStatusFilter = z.enum(["active", "disabled", "all"]);
export type WalletTokenStatusFilter = z.infer<typeof WalletTokenStatusFilter>;

export const WalletTokenListQuery = z.object({
  chainId: ChainIdSchema.optional(),
  /**
   * Super-admin-only cross-tenant filter. Ignored for tenant-scoped callers
   * (their scope already pins the accountId). When the super admin's UI
   * picks an account from the account dropdown, the id flows through here.
   */
  accountId: z.string().min(1).max(64).optional(),
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  // 200-row hard cap defends against the freeze-on-1000-rows incident
  // (see TxListQuery.pageSize for the same fix). CSV exports do their own
  // chunked paging via `cursorRows` and bypass this schema, so the limit
  // here only constrains HTTP query strings.
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  includeDeleted: z.coerce.boolean().default(false),
  /**
   * Lifecycle filter surfaced to the UI as the Active / Disabled tabs.
   * Default `active` preserves the historical list behaviour; admin-only for
   * `disabled`/`all` is enforced server-side in `walletService.listWallets`.
   */
  status: WalletTokenStatusFilter.default("active"),
  export: z.enum(["csv"]).optional(),
  /**
   * Admin-only bypass of the app-wide visible-tokens allow-list. Omitted /
   * undefined behaves as false, so the filter applies by default on every
   * role. The route layer enforces the admin-only gate — a viewer who passes
   * `?includeHidden=true` is silently coerced back to filtered results.
   */
  includeHidden: z.coerce.boolean().optional(),
});
export type WalletTokenListQuery = z.infer<typeof WalletTokenListQuery>;

export const WalletTokenDto = z.object({
  id: z.string(),
  name: z.string(),
  chainId: z.string(),
  address: z.string(),
  custody: z.string(),
  createdAt: z.string(), // ISO-8601
  deletedAt: z.string().nullable(),
  tokensCount: z.number().int().min(0).optional(),
  /**
   * Sum of `balanceRaw × FxRate(USD) / 10^decimals` across every WalletToken
   * in the wallet, rendered as a fixed-point decimal string with 2 fractional
   * digits. `null` when no tokens have FX coverage yet — the UI surfaces an
   * em-dash in that case. Optional so older fixtures + the Wallet Detail
   * response (which still uses `tokensCount`) keep parsing cleanly.
   */
  totalUsd: z.string().nullable().optional(),
  /**
   * Top-3 token symbols by USD value (DESC). Drives the Wallets list Token
   * column. `valueUsd` mirrors {@link WalletDto.totalUsd} — fixed-point 2-dp
   * decimal string, or `null` when no FX rate is available for the token.
   * When FX can't be computed (no rate repo, zero holdings), entries may
   * still be present sorted by `balanceRaw` magnitude instead. Always an
   * array: empty `[]` means the wallet has no tokens yet. Optional on the
   * wire so older fixtures keep parsing.
   */
  topTokens: z
    .array(
      z
        .object({
          symbol: z.string(),
          valueUsd: z.string().nullable().optional(),
        })
        .strict(),
    )
    .max(3)
    .optional(),
  /**
   * Super-admin cross-tenant views surface the owning account so the UI can
   * render an "Account" column without a second lookup. Tenant callers never
   * receive these fields (leaking another tenant's account id/name would be
   * a data-isolation break), so both are `optional`.
   */
  accountId: z.string().optional(),
  accountName: z.string().optional(),
  /**
   * H2a — id of the Wallet (group) this token belongs to, or `null` when
   * the token is "Ungrouped". Optional so older fixtures that pre-date the
   * groups feature still parse cleanly.
   */
  walletId: z.string().nullable().optional(),
  /**
   * Phase 2 — id of the explicitly-pinned ZohoConnection for this address,
   * or `null` when the address inherits from its folder / the account
   * default. Optional so DTOs from pre-Zoho fixtures keep parsing cleanly.
   */
  zohoConnectionId: z.string().nullable().optional(),
});
export type WalletTokenDto = z.infer<typeof WalletTokenDto>;

/**
 * Per-wallet (per-address) token holding. Returned inline on
 * `GET /wallets/:id` so the Wallet Detail page can render token cards + power
 * the token filter on the transactions list without a second round-trip.
 *
 * Naming note: the H1 refactor renamed the on-the-wire DTO `WalletTokenDto` →
 * `WalletTokenBalanceDto`, so the old "WalletToken" name on this DTO is gone
 * but the DTO's `id` is still the per-(address × token) balance row id.
 *
 * `balanceRaw` is a non-negative decimal string in integer token units —
 * NEVER signed (balances can't be negative). `balanceUpdatedAt` is null until
 * the sync engine writes a snapshot.
 */
export const WalletTokenBalanceDto = z
  .object({
    id: z.string(),
    /**
     * System-wide sequential TokenWallet "account number" — surfaced to
     * users as a stable short reference (e.g. `#10770`). Allocated by the
     * Postgres sequence `wallet_token_balance_code_seq` (start 10770) when
     * the row is first written; never changes. Always present after the
     * 20260428_004_token_wallet_code migration runs.
     */
    code: z.number().int().min(10770),
    walletTokenId: z.string(),
    tokenId: z.string(),
    chainId: z.string(),
    symbol: z.string(),
    standard: z.string().nullable(),
    contractAddress: z.string().nullable(),
    decimals: z.number().int(),
    displayName: z.string(),
    source: z.string(), // "seed" | "auto"
    balanceRaw: z.string(),
    balanceUpdatedAt: z.string().nullable(),
    /**
     * P2 Dashboard: per-token USD value (`balanceRaw × FxRate(USD) /
     * 10^decimals`) rendered as a fixed-point decimal string. Mirrors the
     * nullable/optional pattern used by {@link WalletTokenDto.totalUsd} —
     * optional so fixtures and list responses that don't compute it keep
     * parsing; nullable for the explicit "no FX rate" case. The dashboard
     * expand row uses this to filter tokens ≥ $10 client-side without a
     * second trip.
     */
    usdValue: z.string().nullable().optional(),
  })
  .strict();
export type WalletTokenBalanceDto = z.infer<typeof WalletTokenBalanceDto>;

/**
 * Wallet detail response — extends {@link WalletTokenDto} with the caller's
 * held token balances. Native token sorts first, then by symbol asc.
 */
export const WalletTokenDetailDto = WalletTokenDto.extend({
  walletTokenBalances: z.array(WalletTokenBalanceDto),
}).strict();
export type WalletTokenDetailDto = z.infer<typeof WalletTokenDetailDto>;

/* -------------------------------------------------------------------------- */
/*  H2a — Wallet Groups                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Human-facing name for a Wallet (group). Same bounds as the token name
 * since the two live side-by-side in the UI and we don't want one to be
 * shorter/longer for no reason.
 */
export const WalletNameSchema = z.string().trim().min(1).max(80);

/**
 * Wire shape for a Wallet **group** — a folder of WalletTokens. Introduced
 * in H2a after the earlier `Wallet` DTO was renamed to {@link WalletTokenDto}
 * (addresses). `walletTokenCount` is the number of non-soft-deleted
 * WalletTokens currently linked to the group; the UI surfaces it as the
 * "tokens" badge on each row.
 */
export const WalletDto = z
  .object({
    id: z.string(),
    name: z.string(),
    walletTokenCount: z.number().int().min(0),
    createdAt: z.string(), // ISO-8601
    createdBy: z.string().optional(),
    deletedAt: z.string().nullable().optional(),
    /**
     * Phase 1 — id of the parent folder this folder is nested under, or
     * `null` for top-level folders. Always present on the wire so the UI
     * can reliably distinguish "no parent info on this row" (would be
     * undefined) from "explicit top-level" (null).
     */
    parentId: z.string().nullable(),
    /**
     * Phase 2 — id of the explicitly-pinned ZohoConnection for this folder,
     * or `null` when the folder inherits from the account default. Optional
     * so DTOs from pre-Zoho fixtures keep parsing cleanly. Mirrors the
     * matching field on {@link WalletTokenDto}.
     */
    zohoConnectionId: z.string().nullable().optional(),
  })
  .strict();
export type WalletDto = z.infer<typeof WalletDto>;

export const WalletCreateDto = z
  .object({
    name: WalletNameSchema,
    /**
     * Optional parent folder. Omitted or `null` creates a top-level folder;
     * a string id nests the new folder under that parent. The server
     * validates the parent belongs to the caller's account.
     */
    parentId: z.string().min(1).nullable().optional(),
  })
  .strict();
export type WalletCreateDto = z.infer<typeof WalletCreateDto>;

export const WalletPatchDto = z
  .object({
    name: WalletNameSchema.optional(),
    parentId: z.string().min(1).nullable().optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.parentId !== undefined, {
    message: "Provide name or parentId.",
  });
export type WalletPatchDto = z.infer<typeof WalletPatchDto>;

/**
 * Body for `PATCH /wallets/:id/move`. `parentId: null` moves the folder to
 * the top level; a string id nests it under that folder. Cycle prevention
 * (self-loop, descendant-as-parent, cross-account) is enforced server-side.
 */
export const WalletMoveDto = z
  .object({
    parentId: z.string().min(1).nullable(),
  })
  .strict();
export type WalletMoveDto = z.infer<typeof WalletMoveDto>;

/**
 * One step in a folder's ancestor chain. Returned by
 * `GET /wallets/:id/breadcrumbs` ordered top → self.
 */
export const WalletBreadcrumbItem = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .strict();
export type WalletBreadcrumbItem = z.infer<typeof WalletBreadcrumbItem>;

export const WalletBreadcrumbsResponse = z
  .object({
    items: z.array(WalletBreadcrumbItem),
  })
  .strict();
export type WalletBreadcrumbsResponse = z.infer<typeof WalletBreadcrumbsResponse>;

/**
 * `DELETE /api/v1/wallets/:id` — `cascade: true` soft-deletes every child
 * WalletToken so their transaction history stays queryable; `cascade: false`
 * un-groups the tokens (walletId → null). The route accepts `cascade` either
 * in the JSON body or the querystring (DELETE-with-body is valid per RFC
 * 9110, but URL params are the safer cross-client option), so the only
 * schema we expose is the querystring one — body presence is enforced
 * by the route handler.
 */
export const WalletDeleteQuery = z
  .object({
    cascade: z
      .union([z.boolean(), z.string()])
      .transform((v) => {
        if (typeof v === "boolean") return v;
        const s = v.toLowerCase();
        return s === "true" || s === "1";
      })
      .optional(),
  })
  .strict();
export type WalletDeleteQuery = z.infer<typeof WalletDeleteQuery>;

export const WalletListResponse = z
  .object({
    items: z.array(WalletDto),
  })
  .strict();
export type WalletListResponse = z.infer<typeof WalletListResponse>;

/* -------------------------------------------------------------------------- */
/*  M7 — Sync jobs                                                            */
/* -------------------------------------------------------------------------- */

/**
 * `queued` — row persisted, BullMQ job enqueued, worker hasn't picked it up.
 * `running` — worker is processing (startedAt is set).
 * `ok` — finished successfully (finishedAt is set).
 * `error` — terminal failure (errorCode/error describe it).
 * `rate_limited` — provider threw RateLimitError; job is terminal but not a
 *   retryable `error`. UI surfaces this prominently (spec §8.3).
 */
export const SyncJobStatus = z.enum([
  "queued",
  "running",
  "ok",
  "error",
  "rate_limited",
]);
export type SyncJobStatus = z.infer<typeof SyncJobStatus>;

export const SyncJobMode = z.enum(["delta", "full"]);
export type SyncJobMode = z.infer<typeof SyncJobMode>;

export const SyncJobDto = z
  .object({
    id: z.string(),
    walletTokenId: z.string().nullable(),
    mode: SyncJobMode,
    status: SyncJobStatus,
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    txsIngested: z.number().int(),
    errorCode: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
    retryAfterSec: z.number().int().nullable().optional(),
    // v1.4 — when status="running" and the rate-limit bucket has paused the
    // sync, this carries the ISO instant we expect to resume. Cleared on
    // terminal state. Drives the Sync Jobs page's "Running · Throttled" badge.
    throttledUntil: z.string().datetime().nullable().optional(),
  })
  .strict();
export type SyncJobDto = z.infer<typeof SyncJobDto>;

/**
 * Throttle outcomes shared by the per-wallet `POST /sync` response and the
 * `POST /sync-all` `skippedDetails` array (Feature 2). The two reasons are
 * the only states the F2 throttle ever emits; documenting them as a strict
 * enum so the UI can branch on each:
 *   - `recently_synced` — last successful sync is inside the cool-down
 *     window (`AppSetting.minSyncIntervalSeconds`).
 *   - `currently_running` — a job for this wallet is already queued/running.
 */
export const SyncThrottleReason = z.enum([
  "recently_synced",
  "currently_running",
]);
export type SyncThrottleReason = z.infer<typeof SyncThrottleReason>;

/**
 * Per-wallet skip detail surfaced by `/sync-all`. Returned for every wallet
 * the throttle held back so the UI can render a tooltip / drawer with the
 * exact reason. Kept loose on `lastFinishedAt` / `ageSeconds` (optional)
 * because the `currently_running` reason has no `finishedAt`.
 */
export const SyncSkippedDetail = z
  .object({
    walletId: z.string(),
    reason: SyncThrottleReason,
    lastFinishedAt: z.string().datetime().nullable().optional(),
    ageSeconds: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SyncSkippedDetail = z.infer<typeof SyncSkippedDetail>;

/**
 * Successful per-wallet enqueue path. Status is always `queued` here — the
 * F2 throttle takes over for the skipped path below.
 */
export const SyncEnqueueResponse = z
  .object({
    syncJobId: z.string(),
    status: SyncJobStatus,
  })
  .strict();
export type SyncEnqueueResponse = z.infer<typeof SyncEnqueueResponse>;

/**
 * Per-wallet THROTTLED response (F2). Returned with HTTP 200 — being inside
 * the cool-down is a routine outcome, not an error. Includes enough metadata
 * for the toast to read "Skipped — synced 2 minutes ago, retry in 3 min".
 */
export const SyncSkippedResponse = z
  .object({
    status: z.literal("skipped"),
    reason: SyncThrottleReason,
    lastFinishedAt: z.string().datetime().nullable().optional(),
    ageSeconds: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SyncSkippedResponse = z.infer<typeof SyncSkippedResponse>;

export const SyncAllEnqueueResponse = z
  .object({
    enqueued: z.number().int(),
    syncJobIds: z.array(z.string()),
    // F2 — both fields added for the throttle. `skipped` is the count;
    // `skippedDetails` carries the per-wallet reason so the UI can drill
    // into "which wallets and why". Optional so existing clients that read
    // only `enqueued` / `syncJobIds` continue to parse responses cleanly
    // during the rollout window.
    skipped: z.number().int().nonnegative().optional(),
    skippedDetails: z.array(SyncSkippedDetail).optional(),
  })
  .strict();
export type SyncAllEnqueueResponse = z.infer<typeof SyncAllEnqueueResponse>;

export const SyncJobListQuery = z.object({
  walletTokenId: z.string().optional(),
  status: SyncJobStatus.optional(),
  mode: SyncJobMode.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  export: z.enum(["csv"]).optional(),
});
export type SyncJobListQuery = z.infer<typeof SyncJobListQuery>;

export const SyncJobListResponse = z.object({
  items: z.array(SyncJobDto),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
});
export type SyncJobListResponse = z.infer<typeof SyncJobListResponse>;

export const LatestSyncResponse = z
  .object({
    job: SyncJobDto.nullable(),
  })
  .strict();
export type LatestSyncResponse = z.infer<typeof LatestSyncResponse>;

/* -------------------------------------------------------------------------- */
/*  M9 — Transactions                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A transaction affects a single WalletToken in one of three ways relative to
 * the wallet we care about:
 *  - `in`   — counterparty → wallet (positive signed amount)
 *  - `out`  — wallet → counterparty (negative signed amount)
 *  - `self` — a self-transfer (amount is typically zero for ERC-20 moves of
 *             the same token, or the native-gas line for a self-send)
 *
 * The sign lives on `amountRaw` so the UI never needs chain-aware math.
 */
export const TxDirection = z.enum(["in", "out", "self"]);
export type TxDirection = z.infer<typeof TxDirection>;

/**
 * Only terminal statuses are persisted (mempool txs are never written —
 * see spec §8.2). `failed` rows exist so the UI can surface failed sends.
 */
export const TxStatus = z.enum(["confirmed", "failed"]);
export type TxStatus = z.infer<typeof TxStatus>;

/**
 * Query parameters accepted by every transaction list endpoint. The routes
 * layer forces some fields based on the URL (e.g. `walletTokenId` is pinned
 * from the `:id` path param on `/wallet-tokens/:id/transactions`).
 *
 * Amount bounds are decimal strings in integer token units (matching
 * `amountRaw`) to avoid losing precision over the wire for 256-bit values.
 */
/**
 * Coerce a CSV (or already-parsed array) into a non-empty string array.
 * Empty string / empty array → undefined so the repo can short-circuit
 * the `IN` clause.
 */
const CsvOrArray = z
  .union([z.string(), z.array(z.string())])
  .transform((v) => {
    const arr = Array.isArray(v) ? v : v.split(",");
    const cleaned = arr.map((s) => s.trim()).filter((s) => s.length > 0);
    return cleaned.length > 0 ? cleaned : undefined;
  })
  .optional();

export const TxListQuery = z.object({
  walletTokenId: z.string().optional(),
  walletTokenIds: CsvOrArray,
  walletTokenBalanceId: z.string().optional(),
  chainId: ChainIdSchema.optional(),
  chainIds: CsvOrArray,
  tokenId: z.string().optional(),
  tokenIds: CsvOrArray,
  /**
   * Super-admin-only cross-tenant filter. Mirrors `WalletListQuery.accountId` —
   * ignored for tenant-scoped callers.
   */
  accountId: z.string().min(1).max(64).optional(),
  direction: TxDirection.optional(),
  status: TxStatus.optional(),
  from: z.string().datetime().optional(), // ISO blockTime lower bound
  to: z.string().datetime().optional(), // upper bound
  minAmount: z
    .string()
    .regex(/^-?\d+$/, "integer token units expected")
    .optional(),
  maxAmount: z
    .string()
    .regex(/^-?\d+$/, "integer token units expected")
    .optional(),
  tag: z.string().trim().min(1).max(40).optional(),
  tags: CsvOrArray,
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  /**
   * Hard cap at 200 rows. A super-admin once selected 1000 rows in the UI
   * and froze the page for many seconds — both the Phase A GROUP BY +
   * Phase B component fetch and the DataTable render bottleneck on large
   * pages. CSV exports paginate internally via `cursorRows` and bypass
   * this schema, so streaming is unaffected.
   */
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.string().trim().max(40).default("blockTime.desc"),
  /**
   * M12 — when set to "csv" the route streams the filter-matching rows as
   * CSV instead of returning paginated JSON. Pagination (`page`, `pageSize`)
   * is ignored for CSV per spec §14 AC.
   */
  export: z.enum(["csv"]).optional(),
  /**
   * Bypass the admin-configured "visible tokens" allow-list. Omitted /
   * undefined behaves as false, so the app-wide filter applies by default.
   * CSV exports opt in when an admin wants the full history even for tokens
   * hidden from the UI.
   */
  includeHidden: z.coerce.boolean().optional(),
  /**
   * v1.2 — list shape. `"raw"` (default) preserves the pre-v1.2 behavior so
   * existing scripts and tests that hit `/transactions` without the field keep
   * returning `TxListResponse`. The web client explicitly sends `view=grouped`
   * to receive a {@link TxGroupListResponse} instead. See spec §16.10.
   */
  view: z.enum(["grouped", "raw"]).default("raw"),
  /**
   * v1.2 — when false (default), the repo + service layer filter out groups
   * whose tokens are effectively spam OR that have no non-zero asset movement.
   * `true` returns all rows; the UI surfaces this as the Show-spam toggle.
   *
   * Note: `z.coerce.boolean()` is unsafe for query strings because
   * `Boolean("false")` is `true` in JS. We accept string literals explicitly.
   */
  showSpam: queryBool().default(false),
  /**
   * v1.3 — when true (default), the grouped list hides purely-incoming groups
   * whose primary-movement USD value is below the caller's account dust
   * threshold. Ignored in `view=raw`. Clients flip this to `false` to surface
   * hidden groups (badge rendered via `TxGroupDto.dust.effective`).
   */
  hideDust: queryBool().default(true),
});
export type TxListQuery = z.infer<typeof TxListQuery>;

/**
 * Wire shape for a single transaction. `amountRaw` / `feeRaw` / `blockNumber`
 * are stringified because BigInt + IEEE-754 numbers can't safely round-trip
 * via JSON. The server computes `explorerUrl` by substituting `{hash}` into
 * `Chain.explorerTxUrlTemplate` (spec §8.4 / req 4) so the UI never needs
 * per-chain conditionals.
 */
export const TxAnnotationShape = z
  .object({
    comment: z.string().nullable(),
    tags: z.array(z.string()),
    updatedBy: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type TxAnnotationShape = z.infer<typeof TxAnnotationShape>;

export const TxDto = z.object({
  id: z.string(),
  walletTokenId: z.string(),
  walletTokenName: z.string(),
  walletTokenBalanceId: z.string(),
  chainId: z.string(),
  tokenId: z.string(),
  tokenSymbol: z.string(),
  tokenDecimals: z.number().int(),
  txHash: z.string(),
  blockNumber: z.string(),
  blockTime: z.string(),
  direction: TxDirection,
  counterparty: z.string().nullable(),
  amountRaw: z.string(),
  feeRaw: z.string().nullable(),
  status: TxStatus,
  explorerUrl: z.string(),
  annotation: TxAnnotationShape.nullable(),
  /**
   * M14 — signed USD/EUR amount derived server-side from `amountRaw`,
   * `tokenDecimals`, and the latest FxRate for this token. Fixed-point
   * decimal string (up to 4 fractional digits, sign preserved) to avoid a
   * second unit-of-measure confusion. `null` when no rate is available.
   */
  amountUsdRaw: z.string().nullable(),
  amountEurRaw: z.string().nullable(),
  /** ISO timestamp of the FxRate row used. `null` when the value is null. */
  fxAsOfUsd: z.string().nullable(),
  fxAsOfEur: z.string().nullable(),
  /**
   * Super-admin cross-tenant views surface the owning account so the UI can
   * render an "Account" column without a second lookup. Tenant callers never
   * receive these fields (leaking another tenant's account id/name would be
   * a data-isolation break), so both are `optional`.
   */
  accountId: z.string().optional(),
  accountName: z.string().optional(),
  /**
   * v1.5 — latest memo for this transaction (newest by createdAt). `null`
   * when no memos exist. Only this single row is shipped in list payloads;
   * the full timeline lives on TxDetailDto/TxGroupDto.
   */
  latestMemo: z.lazy(() => TxMemoSchema).nullable().optional(),
});
export type TxDto = z.infer<typeof TxDto>;

export const TxListResponse = z
  .object({
    items: z.array(TxDto),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    sort: z.string(),
  })
  .strict();
export type TxListResponse = z.infer<typeof TxListResponse>;

/**
 * Single-tx detail endpoint extends {@link TxDto} with the verbatim provider
 * payload for the Debug panel (spec §9.1 tx-detail drawer).
 */
/* -------------------------------------------------------------------------- */
/*  v1.5 — Drawer extras (memos, owner, activity, linked records)              */
/* -------------------------------------------------------------------------- */

export const TxActivityEventActionSchema = z.enum([
  "tag_added",
  "tag_removed",
  "memo_changed",
  "owner_changed",
  "spam_marked",
  "spam_cleared",
  "ingested",
  "linked",
]);
export type TxActivityEventActionSchema = z.infer<
  typeof TxActivityEventActionSchema
>;

export const TxLinkedRecordKindSchema = z.enum([
  "netsuite",
  "qbo",
  "stripe",
  "slack",
  "manual",
]);
export type TxLinkedRecordKindSchema = z.infer<typeof TxLinkedRecordKindSchema>;

export const TxAuthorSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
});
export type TxAuthorSchema = z.infer<typeof TxAuthorSchema>;

export const TxMemoSchema = z.object({
  id: z.string(),
  body: z.string(),
  author: TxAuthorSchema,
  createdAt: z.string(),
  groupKey: z.string().nullable(),
});
export type TxMemoSchema = z.infer<typeof TxMemoSchema>;

export const TxOwnerSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(["ADMIN", "MANAGER", "VIEWER"]),
  assignedAt: z.string(),
});
export type TxOwnerSchema = z.infer<typeof TxOwnerSchema>;

export const TxActivityEventSchema = z.object({
  id: z.string(),
  actor: z
    .object({
      userId: z.string(),
      email: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
  action: TxActivityEventActionSchema,
  at: z.string(),
  meta: z.record(z.unknown()).optional(),
});
export type TxActivityEventSchema = z.infer<typeof TxActivityEventSchema>;

export const TxLinkedRecordSchema = z.object({
  id: z.string(),
  kind: TxLinkedRecordKindSchema,
  title: z.string(),
  externalUrl: z.string().nullable(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  createdAt: z.string(),
});
export type TxLinkedRecordSchema = z.infer<typeof TxLinkedRecordSchema>;

export const TxDetailDto = TxDto.extend({
  rawPayload: z.unknown(),
  fromAddress: z.string().nullable().optional(),
  toAddress: z.string().nullable().optional(),
  memos: z.array(TxMemoSchema).optional(),
  owner: TxOwnerSchema.nullable().optional(),
  activityEvents: z.array(TxActivityEventSchema).optional(),
  linkedRecords: z.array(TxLinkedRecordSchema).optional(),
});
export type TxDetailDto = z.infer<typeof TxDetailDto>;

/* -------------------------------------------------------------------------- */
/*  M11 — Annotations                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Tags must be short, printable, and dedupe-friendly. The regex requires a
 * leading alphanumeric to prevent `""`, `" "`, or `"-foo"` from sneaking past
 * the `.trim().min(1)` gate. Server-side we further lowercase + dedupe, but
 * the client uses this same schema so validation feels identical in both
 * places.
 */
export const TagSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9][A-Za-z0-9 _\-\/\.:]*$/);

export const CommentSchema = z.string().max(2000);

/**
 * Upsert body for `PUT /transactions/:id/annotation`. Enforces the at-least-
 * one-of rule at the schema boundary — the service can safely assume a
 * non-empty input.
 */
export const AnnotationUpsertDto = z
  .object({
    comment: CommentSchema.nullable().optional(),
    tags: z.array(TagSchema).max(20).optional(),
  })
  .strict()
  .refine(
    (v) =>
      (typeof v.comment === "string" && v.comment.trim().length > 0) ||
      (Array.isArray(v.tags) && v.tags.length > 0),
    { message: "At least one of comment or tags must be non-empty." },
  );
export type AnnotationUpsertDto = z.infer<typeof AnnotationUpsertDto>;

/**
 * Wire shape for a persisted annotation. Matches the `annotation` sub-object
 * inside {@link TxDto} so the editor can round-trip without a second fetch.
 */
export const AnnotationDto = z
  .object({
    transactionId: z.string(),
    comment: z.string().nullable(),
    tags: z.array(z.string()),
    updatedBy: z.string(),
    updatedAt: z.string(), // ISO-8601
  })
  .strict();
export type AnnotationDto = z.infer<typeof AnnotationDto>;

/**
 * `GET /meta/tags` — distinct tags visible to the caller with their occurrence
 * count. Sorted by count desc so the autocomplete UI can show "most-used"
 * suggestions first. Bounded at 200 rows server-side.
 */
export const MetaTagsResponse = z
  .object({
    tags: z.array(
      z.object({
        tag: z.string(),
        count: z.number().int().min(0),
      }),
    ),
  })
  .strict();
export type MetaTagsResponse = z.infer<typeof MetaTagsResponse>;

/* -------------------------------------------------------------------------- */
/*  M13 — User Table Settings                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `tableKey` identifies a table surface (e.g. `wallets`, `transactions_global`,
 * `sync_jobs`). Kept narrow — only alphanumeric, `_`, and `-` — so the key is
 * safe to embed in URLs and Prisma queries without escaping. Wallet-scoped keys
 * use `wallet:<id>:transactions` shape at the call site but we deliberately
 * DON'T allow `:` here; the web normalizes wallet-scoped keys with `_` instead
 * (see `apps/web/src/lib/userTableSettings.ts`).
 */
export const TableKeySchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-zA-Z0-9_\-]+$/);

/**
 * Persisted per-user, per-table UI settings. `settings` is intentionally
 * free-form JSON — the server stores and echoes it back without inspection so
 * the UI can evolve its shape without a migration. The web revalidates its own
 * shape on load (see `useTableSettings`).
 */
export const UserTableSettingDto = z
  .object({
    tableKey: TableKeySchema,
    settings: z.record(z.unknown()),
    updatedAt: z.string(),
  })
  .strict();
export type UserTableSettingDto = z.infer<typeof UserTableSettingDto>;

/**
 * Body for `PUT /user-table-settings/:tableKey`. Only the `settings` payload
 * is accepted from the client — the server derives `updatedAt` and echoes the
 * key from the URL.
 */
export const UserTableSettingPutDto = z
  .object({
    settings: z.record(z.unknown()),
  })
  .strict();
export type UserTableSettingPutDto = z.infer<typeof UserTableSettingPutDto>;

/* -------------------------------------------------------------------------- */
/*  Admin — Visible tokens                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Admin-managed allow-list of `Token.id`s that are visible across the app.
 * An empty array is the documented "no filter" sentinel — wallet detail,
 * wallet-list totals, and transaction lists all show every token when the
 * list is empty so fresh installs keep their out-of-box behavior.
 *
 * Individual ids are bounded (1..80 chars) so a tampered payload can't smuggle
 * unbounded text into the Postgres `text[]` column.
 */
export const VisibleTokensDto = z
  .object({
    tokenIds: z.array(z.string().min(1).max(80)),
  })
  .strict();
export type VisibleTokensDto = z.infer<typeof VisibleTokensDto>;

/* -------------------------------------------------------------------------- */
/*  P2 — Dashboard                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Per-wallet sync status surfaced on the dashboard. Superset of
 * {@link SyncJobStatus} (the five persisted `SyncJob.status` values) plus
 * `"idle"` — the synthetic "no SyncJob row exists for this wallet yet" state.
 * The existing {@link SyncJobStatus} is kept as the canonical DB-row status;
 * this enum is the wire shape specifically for the dashboard per-wallet map.
 */
export const WalletSyncStatus = z.enum([
  "ok",
  "running",
  "queued",
  "error",
  "rate_limited",
  "idle",
]);
export type WalletSyncStatus = z.infer<typeof WalletSyncStatus>;

/**
 * Traffic-light rollup used both per-wallet and at the dashboard level.
 * Derivation rules live in `dashboardService` — roughly: green = fresh ok,
 * yellow = stale ok or rate-limited, red = error or no successful job.
 */
export const SyncHealth = z.enum(["green", "yellow", "red"]);
export type SyncHealth = z.infer<typeof SyncHealth>;

/**
 * Entry in {@link DashboardSummaryDto.walletSyncStatus}. `finishedAt` is the
 * latest SyncJob.finishedAt for the wallet (ISO-8601) or `null` for `idle`
 * (no job) and in-flight (`running`/`queued`) states.
 */
export const WalletSyncStatusEntry = z
  .object({
    status: WalletSyncStatus,
    finishedAt: z.string().nullable(),
  })
  .strict();
export type WalletSyncStatusEntry = z.infer<typeof WalletSyncStatusEntry>;

/**
 * Response shape for `GET /accounts/:accountId/dashboard`. Composed by
 * `dashboardService.summarize` — every field is derived from existing tables
 * (no Prisma schema changes). `memberCount` is null when the caller is not
 * an ADMIN on the account (the admin-only section of the dashboard hides).
 */
export const DashboardSummaryDto = z
  .object({
    /** Max SyncJob.finishedAt where status='ok' across wallets in scope. */
    lastSyncedAt: z.string().nullable(),
    /** Worst-of rollup across wallets — `red` > `yellow` > `green`. */
    syncHealth: SyncHealth,
    /** walletTokenId -> max Transaction.blockTime (ISO) or `null` if no txs. */
    walletLastTx: z.record(z.string(), z.string().nullable()),
    /** walletTokenId -> latest SyncJob status + finishedAt. */
    walletSyncStatus: z.record(z.string(), WalletSyncStatusEntry),
    /** Non-negative count, or `null` when the caller is not ADMIN. */
    memberCount: z.number().int().min(0).nullable(),
  })
  .strict();
export type DashboardSummaryDto = z.infer<typeof DashboardSummaryDto>;

/* -------------------------------------------------------------------------- */
/*  v1.2 — Economic Transaction View + Spam Filtering                         */
/* -------------------------------------------------------------------------- */

/**
 * Group "type" assigned by the per-chain classifier at sync time.
 *
 * - `transfer`       — plain in/out/self transfer of a single asset (the
 *                      default when no more specific rule matches).
 * - `swap`           — a DEX/aggregator swap (native in + token out, or two
 *                      token movements in opposite directions).
 * - `approve`        — an ERC-20/TRC-20 approve call with no asset movement.
 * - `contract_call`  — a catch-all for non-value contract interactions.
 * - `failed`         — every component is `status="failed"`.
 * - `self`           — a self-transfer (wallet sends to itself).
 */
export const TxGroupTypeSchema = z.enum([
  "transfer",
  "swap",
  "approve",
  "contract_call",
  "failed",
  "self",
]);

/**
 * Persisted protocol label for a (chainId, txHash). `protocol="unknown"`
 * when the classifier had no matching rule; `action` falls back to the
 * structural group type so the UI always has something to render.
 */
export const TxProtocolLabelSchema = z
  .object({
    protocol: z.string(),
    action: z.string(),
  })
  .strict();

/**
 * Primary movement exposed by the grouped row. Selection rule (spec §16.5
 * "Amount"): largest `|USD|` non-native component, else the native row.
 * Amount values are decimal strings in integer token units (signed so the
 * UI can render direction without per-chain math).
 */
export const TxGroupPrimarySchema = z
  .object({
    tokenId: z.string(),
    tokenSymbol: z.string(),
    tokenDecimals: z.number().int(),
    amountRaw: z.string(),
    amountUsdRaw: z.string().nullable(),
    amountEurRaw: z.string().nullable(),
  })
  .strict();

/**
 * Fee movement — gas paid in the chain's native asset on the wallet's
 * outgoing side. `null` when the wallet wasn't the payer (incoming-only
 * groups, BTC passthroughs where fee is not broken out, etc.). `amountRaw`
 * is positive (absolute) — the grouped row treats "fee" as an always-paid
 * quantity independent of direction semantics.
 *
 * `amountUsdRaw` / `amountEurRaw` price the fee in each fiat at the tx's
 * block-day rate — mirroring `TxGroupPrimarySchema`'s dual-currency fields so
 * the Fee column can render "≈ $x / €x" in the account's preferred currency.
 * Either is `null` when that day's FX rate for the fee's native token is
 * missing (honest omission — the UI shows the native amount only, never
 * "≈ —"). The fee is priced in its OWN native token (e.g. TRX for a
 * USDT-TRC20 transfer), not the primary movement's token.
 */
export const TxGroupFeeSchema = z
  .object({
    tokenId: z.string(),
    tokenSymbol: z.string(),
    tokenDecimals: z.number().int(),
    amountRaw: z.string(),
    amountUsdRaw: z.string().nullable(),
    amountEurRaw: z.string().nullable(),
  })
  .strict();

/**
 * Spam resolution surfaced on every grouped row so the UI can render the
 * correct Mark-valid / Mark-spam action without a second fetch.
 *
 * - `effective`       — the answer the filter uses: override wins over global.
 * - `global`          — `Token.isSpam` as of the query time.
 * - `accountOverride` — `AccountTokenSpamOverride.verdict` for (account,
 *                       token) or null when no override exists. Always null
 *                       on super-admin scope (overrides are bypassed).
 * - `primaryReason`   — human-facing label for the primary token's spam
 *                       classification (e.g. `"auto_airdrop"`) or null.
 */
export const TxGroupSpamSchema = z
  .object({
    effective: z.boolean(),
    global: z.boolean(),
    accountOverride: z.enum(["valid", "spam"]).nullable(),
    primaryReason: z.string().nullable(),
  })
  .strict();

/**
 * v1.3 dust verdict attached to every grouped row. `effective=true` means the
 * group would be hidden in `hideDust=true` mode; the UI uses it to render a
 * "dust" badge when `hideDust=false` flips hidden rows back into view.
 *
 * - `effective`  — the answer the filter uses (SQL HAVING mirrors it).
 * - `reason`     — currently only `"below_threshold"`; reserved so we can add
 *                  counterparty-level heuristics in v1.4 without a wire break.
 */
export const TxGroupDustSchema = z
  .object({
    effective: z.boolean(),
    reason: z.enum(["below_threshold"]).nullable(),
  })
  .strict();

/**
 * Server-assembled grouped transaction row. Keyed by `(chainId, txHash)` —
 * `groupKey` is the URL-safe encoding (`${chainId}:${txHash}`) used by the
 * detail endpoint.
 *
 * `annotationTargetTxId` is the id of the representative component used by
 * the drawer when the user writes an annotation from the grouped view
 * (native row → `out` fallback → first component). The hash-keyed
 * `AnnotationByHash` store preserves that annotation across re-syncs.
 */
export const TxGroupDto = z.object({
  groupKey: z.string(),
  chainId: z.string(),
  txHash: z.string(),
  blockNumber: z.string(),
  blockTime: z.string(),
  type: TxGroupTypeSchema,
  protocol: TxProtocolLabelSchema,
  direction: TxDirection,
  counterparty: z.string().nullable(),
  primary: TxGroupPrimarySchema,
  fee: TxGroupFeeSchema.nullable(),
  status: TxStatus,
  spam: TxGroupSpamSchema,
  dust: TxGroupDustSchema,
  components: z.array(TxDto),
  annotation: TxAnnotationShape.nullable(),
  annotationTargetTxId: z.string(),
  explorerUrl: z.string(),
  accountId: z.string().optional(),
  accountName: z.string().optional(),
  memos: z.array(TxMemoSchema).optional(),
});
export type TxGroupDto = z.infer<typeof TxGroupDto>;

export const TxGroupListResponse = z
  .object({
    items: z.array(TxGroupDto),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
  })
  .strict();
export type TxGroupListResponse = z.infer<typeof TxGroupListResponse>;

/* -------------------------------------------------------------------------- */
/*  Fee summary — aggregate blockchain fees paid across the filtered set       */
/* -------------------------------------------------------------------------- */

/**
 * Query for `GET /transactions/fee-summary`. Reuses every filter axis the
 * transaction list already accepts (wallet, chain, token, tags, direction,
 * status, date range, min/max amount, free-text `q`, super-admin `accountId`,
 * super-admin `includeHidden`) so the strip on top of the list is guaranteed
 * to reflect the same set of rows the list shows. The summary is scoped by
 * the caller's account/scope; `direction` and `feeRaw IS NOT NULL` are
 * enforced INSIDE the summarizer so the caller can't ask for "incoming fees".
 *
 * `currency` is optional: the route falls back to the user's preferred
 * currency when omitted (or USD when the caller has no preference set).
 */
export const TxFeeSummaryQuery = TxListQuery.omit({
  page: true,
  pageSize: true,
  sort: true,
  export: true,
  view: true,
}).extend({
  currency: PreferredCurrencySchema.optional(),
});
export type TxFeeSummaryQuery = z.infer<typeof TxFeeSummaryQuery>;

/**
 * Per-chain roll-up of fees paid. `feeNativeRaw` is a bigint-as-string so a
 * chain's 256-bit total survives the wire hop. `feeFiat` is a JSON `number`
 * (fees fit comfortably in float64 at any realistic volume) and is `null`
 * when at least one (chain, day) bucket in this chain's contribution lacked
 * an FX rate — we surface uncertainty rather than fabricating a partial total.
 */
export const TxFeeSummaryPerChain = z
  .object({
    chainId: z.string(),
    nativeTokenId: z.string(),
    nativeSymbol: z.string(),
    nativeDecimals: z.number().int().nonnegative(),
    feeNativeRaw: z.string(),
    feeFiat: z.number().nullable(),
    txCount: z.number().int().nonnegative(),
  })
  .strict();
export type TxFeeSummaryPerChain = z.infer<typeof TxFeeSummaryPerChain>;

/**
 * `GET /transactions/fee-summary` response. `totalFiat` is `null` when ANY
 * `perChain` entry is `null` — the total is a lower bound in that case and
 * we prefer to draw that uncertainty in the UI over silently understating.
 *
 * `currency` is echoed back so callers can render the symbol without an
 * extra lookup, and so the response is self-describing when cached.
 */
export const TxFeeSummaryResponse = z
  .object({
    currency: PreferredCurrencySchema,
    perChain: z.array(TxFeeSummaryPerChain),
    totalFiat: z.number().nullable(),
    txCount: z.number().int().nonnegative(),
  })
  .strict();
export type TxFeeSummaryResponse = z.infer<typeof TxFeeSummaryResponse>;

/**
 * Account-scoped spam override row. Returned verbatim by the list + upsert
 * endpoints; also carries `tokenSymbol` + `chainId` so the admin UI can
 * render the table without a second meta lookup.
 */
export const SpamOverrideDto = z
  .object({
    accountId: z.string(),
    tokenId: z.string(),
    tokenSymbol: z.string(),
    chainId: z.string(),
    verdict: z.enum(["valid", "spam"]),
    updatedBy: z.string(),
    /**
     * Email of the user captured in `updatedBy`, resolved server-side so the
     * admin UI can render a human label without a second round-trip. `null`
     * when the user has been deactivated (soft-deleted) or otherwise can't be
     * resolved — the UI falls back to the raw user id in that case. Optional
     * so older wire payloads that pre-date this field still parse cleanly.
     */
    updatedByEmail: z.string().nullable().optional(),
    updatedAt: z.string(),
  })
  .strict();
export type SpamOverrideDto = z.infer<typeof SpamOverrideDto>;

export const SpamOverrideListResponse = z
  .object({
    items: z.array(SpamOverrideDto),
  })
  .strict();
export type SpamOverrideListResponse = z.infer<typeof SpamOverrideListResponse>;

/**
 * Body for `POST /accounts/:accountId/spam-overrides`. The account id comes
 * from the URL; the body only identifies the token + the desired verdict.
 */
export const SpamOverrideUpsertDto = z
  .object({
    tokenId: z.string().min(1),
    verdict: z.enum(["valid", "spam"]),
  })
  .strict();
export type SpamOverrideUpsertDto = z.infer<typeof SpamOverrideUpsertDto>;

/**
 * Body for super-admin `PATCH /admin/tokens/:tokenId/spam`. `spamReason`
 * is derived server-side (`"manual"` when the admin sets `isSpam=true`,
 * cleared on `false`). Audit-logged via `token.spam_update`.
 */
export const TokenSpamPatchDto = z
  .object({
    isSpam: z.boolean(),
  })
  .strict();
export type TokenSpamPatchDto = z.infer<typeof TokenSpamPatchDto>;

/* -------------------------------------------------------------------------- */
/*  Super-admin Verified-Token allowlist                                      */
/* -------------------------------------------------------------------------- */

/**
 * Wire shape for a row in the super-admin Verified Tokens page. Extends the
 * persistence row with the strict-allowlist columns (`verified`, `isSpam`,
 * `spamReason`) so the table can render the toggle + spam badge without a
 * second lookup.
 */
export const TokenAdminDto = z
  .object({
    id: z.string(),
    chainId: z.string(),
    symbol: z.string(),
    standard: z.string().nullable(),
    contractAddress: z.string().nullable(),
    decimals: z.number().int(),
    displayName: z.string(),
    source: z.string(),
    verified: z.boolean(),
    isSpam: z.boolean(),
    spamReason: z.string().nullable(),
  })
  .strict();
export type TokenAdminDto = z.infer<typeof TokenAdminDto>;

export const TokenAdminListQuery = z.object({
  chainId: ChainIdSchema.optional(),
  /** Optional filter on the verified column. Omitted = all rows. */
  verified: z
    .union([z.boolean(), z.string()])
    .transform((v) => {
      if (typeof v === "boolean") return v;
      const s = v.toLowerCase();
      return s === "true" || s === "1";
    })
    .optional(),
  /** Case-insensitive contains match against symbol/displayName/contract. */
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type TokenAdminListQuery = z.infer<typeof TokenAdminListQuery>;

export const TokenAdminListResponse = z
  .object({
    items: z.array(TokenAdminDto),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  })
  .strict();
export type TokenAdminListResponse = z.infer<typeof TokenAdminListResponse>;

/**
 * Per-chain `contractAddress` regexes. We branch the refinement on `chainId`
 * so a typo like `0xDeadBeef` (8 hex chars) gets rejected at the DTO boundary
 * instead of being persisted and silently missing every legitimate
 * sync-discovered row. Future chains with no rule pass through.
 *
 *  - ETH: 20-byte hex, `0x`-prefixed (case-insensitive, no checksum check —
 *    the upstream auto-discoverer normalises casing already).
 *  - TRX: base58 Tron address starting with `T`, total 34 chars.
 *  - BTC: contract addresses are not modelled — only the native token exists,
 *    so the field MUST be null/undefined.
 */
const CONTRACT_ADDRESS_RULES: Record<string, RegExp | "must-be-null"> = {
  ETH: /^0x[a-fA-F0-9]{40}$/,
  TRX: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
  BTC: "must-be-null",
};

/**
 * Body for `POST /api/v1/_superadmin/tokens`. The created row is implicitly
 * `verified=true, isSpam=false, source='seed'` (super-admin curated). The
 * deterministic id format mirrors PrismaSyncStore's auto generator.
 */
export const TokenAdminCreateDto = z
  .object({
    chainId: ChainIdSchema,
    symbol: z.string().trim().min(1).max(40),
    decimals: z.number().int().min(0).max(36),
    displayName: z.string().trim().min(1).max(120),
    standard: z.string().trim().min(1).max(32).optional(),
    /**
     * Native tokens omit the contract; ERC-20 / TRC-20 / ... carry one. The
     * service rejects non-native chains with no contract and rejects native
     * with one. The refinement below additionally enforces a per-chain
     * address format so a typo doesn't slip through to persistence.
     */
    contractAddress: z.string().trim().min(1).max(128).nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const rule = CONTRACT_ADDRESS_RULES[value.chainId];
    if (!rule) return; // future-proof: chains without a rule pass through.
    const addr = value.contractAddress;
    if (rule === "must-be-null") {
      if (addr != null && addr !== "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contractAddress"],
          message: `Chain "${value.chainId}" has no token contracts; contractAddress must be null.`,
        });
      }
      return;
    }
    if (addr == null || addr === "") return; // null contract is the native-token path; service-layer guard handles native vs non-native.
    if (!rule.test(addr)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contractAddress"],
        message: `contractAddress is not a valid ${value.chainId} address.`,
      });
    }
  });
export type TokenAdminCreateDto = z.infer<typeof TokenAdminCreateDto>;

/** Body for `PATCH /api/v1/_superadmin/tokens/:id/verify`. */
export const TokenAdminVerifyDto = z
  .object({
    verified: z.boolean(),
  })
  .strict();
export type TokenAdminVerifyDto = z.infer<typeof TokenAdminVerifyDto>;

/* -------------------------------------------------------------------------- */
/*  Account Settings                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Wire shape for `GET /accounts/:id/settings`. Mirrors the persisted
 * `Account.dustThresholdUsdCents` column verbatim — `null` is the sentinel
 * for "use the app-wide default". The field is an integer number of USD
 * cents so the client never has to do floating-point math for comparison.
 *
 * Additional account-scoped settings will be appended as siblings here so
 * the single endpoint can carry the whole bag without version bumps.
 */
export const AccountSettingsDto = z
  .object({
    dustThresholdUsdCents: z.number().int().nullable(),
  })
  .strict();
export type AccountSettingsDto = z.infer<typeof AccountSettingsDto>;

/**
 * Body for `PUT /accounts/:id/settings`. Strict so unknown fields surface a
 * clean 400 at the route boundary. Bounded at 1,000,000 cents ($10,000) so
 * a fat-fingered UI value can't make the filter hide every transaction the
 * account will ever see.
 */
export const AccountSettingsUpdateDto = z
  .object({
    dustThresholdUsdCents: z.number().int().min(0).max(1_000_000).nullable(),
  })
  .strict();
export type AccountSettingsUpdateDto = z.infer<typeof AccountSettingsUpdateDto>;

/* -------------------------------------------------------------------------- */
/*  Super-admin API keys (mutable provider key store)                         */
/* -------------------------------------------------------------------------- */

/**
 * The exact set of provider keys editable through the super-admin API Keys
 * surface. The names mirror the env-var keys declared in `apps/api/src/config/env.ts`
 * so the resolver can fall back to `process.env[name]` when no DB row exists.
 *
 * Adding a new provider is a single-line change here (plus a new fallback in
 * `apps/api/src/services/apiKeyService.ts`'s whitelist). Endpoint URLs are NOT
 * managed here — only secret keys.
 */
export const ApiKeyName = z.enum([
  "ETHERSCAN_API_KEY",
  "ALCHEMY_API_KEY",
  "TRONGRID_API_KEY",
  // Resend (transactional email) — read by the forgot-password flow.
  // Stored alongside the chain provider keys so a super admin can rotate
  // it from the same surface without a redeploy.
  "RESEND_API_KEY",
  // CryptoCompare — used only by the historical FX backfill script. Listed
  // here so a super admin can rotate it from the same UI as the other keys.
  "CRYPTOCOMPARE_API_KEY",
]);
export type ApiKeyName = z.infer<typeof ApiKeyName>;

/**
 * Wire shape for one row on the super-admin API Keys page.
 *
 * `value` is the raw plaintext secret — the super admin sees the full value
 * (no masking). `null` is the explicit "no row in DB, falling back to env"
 * signal so the UI can render an empty input rather than a blank for both
 * "unset" and "set to empty string".
 *
 * `updatedBy` / `updatedAt` are `null` when no row has been persisted yet
 * (the env-var fallback is the active source).
 */
export const ApiKeyDto = z
  .object({
    name: ApiKeyName,
    value: z.string().nullable(),
    updatedBy: z.string().nullable(),
    updatedAt: z.string().nullable(),
  })
  .strict();
export type ApiKeyDto = z.infer<typeof ApiKeyDto>;

export const ApiKeyListDto = z
  .object({
    items: z.array(ApiKeyDto),
  })
  .strict();
export type ApiKeyListDto = z.infer<typeof ApiKeyListDto>;

/**
 * Body for `PUT /api/v1/admin/api-keys/:name`. Bounded length so a tampered
 * payload can't smuggle pathological strings into the table. Trim at the
 * boundary — operators frequently paste keys with stray whitespace.
 */
export const ApiKeyUpdateDto = z
  .object({
    value: z.string().trim().min(1).max(512),
  })
  .strict();
export type ApiKeyUpdateDto = z.infer<typeof ApiKeyUpdateDto>;

/* -------------------------------------------------------------------------- */
/*  Zoho Books — Phase 1 connection management                                */
/* -------------------------------------------------------------------------- */

/**
 * The Zoho data-center suffix. Each region runs its own accounts server and
 * Books API host: `https://accounts.zoho.<region>/oauth/v2/...` for OAuth,
 * `https://www.zohoapis.<region>/books/v3/...` for Books. The user picks the
 * region during connect; persisted on the `ZohoConnection` row so all
 * subsequent calls hit the correct DC without re-asking.
 */
export const ZohoDcRegionSchema = z.enum([
  "com",
  "eu",
  "in",
  "au",
  "jp",
  "ca",
  "uk",
  "sa",
  "cn",
]);
export type ZohoDcRegion = z.infer<typeof ZohoDcRegionSchema>;

/**
 * Health of the most recent verification probe for a connection.
 *   - `never`  — connection just created, probe hasn't run yet (rare; the
 *                completeOAuth path runs verify inline).
 *   - `ok`     — refresh + organizations + bankaccounts all returned 2xx.
 *   - `error`  — at least one step failed; `lastVerifyError` carries the
 *                operator-readable message.
 */
export const ZohoVerifyStatusSchema = z.enum(["ok", "error", "never"]);
export type ZohoVerifyStatus = z.infer<typeof ZohoVerifyStatusSchema>;

/**
 * Wire shape for one row on the Account Settings → Zoho connections list. The
 * `encryptedRefreshToken` and `accessToken` are NEVER exposed — they only ever
 * live server-side. `lastVerifyError` is operator-readable (e.g.
 * `"invalid_token"`, `"403 forbidden on /organizations"`) so the UI can surface
 * what broke without needing a follow-up request.
 */
export const ZohoConnectionDto = z
  .object({
    id: z.string(),
    accountId: z.string(),
    displayName: z.string(),
    dcRegion: ZohoDcRegionSchema,
    organizationId: z.string(),
    // Cached at connect time from `GET /organizations`. Transiently `null` for
    // a brand-new row where the org lookup hasn't completed yet; the UI
    // renders an em-dash placeholder in that case.
    organizationName: z.string().nullable(),
    isDefault: z.boolean(),
    connectedAt: z.string(), // ISO-8601
    // Nullable because legacy / data-migration rows may lack an attributable
    // user id (e.g. seeded prior to the connectedByUserId column existing).
    connectedByUserId: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    lastVerifiedAt: z.string().nullable(),
    lastVerifyStatus: ZohoVerifyStatusSchema,
    lastVerifyError: z.string().nullable(),
  })
  .strict();
export type ZohoConnectionDto = z.infer<typeof ZohoConnectionDto>;

export const ZohoConnectionListResponse = z
  .object({
    items: z.array(ZohoConnectionDto),
  })
  .strict();
export type ZohoConnectionListResponse = z.infer<
  typeof ZohoConnectionListResponse
>;

/**
 * Body for `POST /accounts/me/zoho/connections` (start OAuth). The user types
 * a display name (e.g. "Vegas Nova LLC books") and picks a DC region BEFORE
 * the OAuth dance so the response can carry a DC-correct authorize URL.
 */
export const ZohoStartOAuthDto = z
  .object({
    displayName: z.string().trim().min(1).max(120),
    dcRegion: ZohoDcRegionSchema,
  })
  .strict();
export type ZohoStartOAuthDto = z.infer<typeof ZohoStartOAuthDto>;

/**
 * Response from `POST /accounts/me/zoho/connections`. `state` is a CSRF token
 * the FE echoes back into the callback URL — the server validates it on the
 * callback path to prevent cross-origin OAuth replay.
 */
export const ZohoStartOAuthResponse = z
  .object({
    authorizeUrl: z.string().url(),
    state: z.string(),
  })
  .strict();
export type ZohoStartOAuthResponse = z.infer<typeof ZohoStartOAuthResponse>;

/**
 * Querystring on `GET /accounts/me/zoho/connections/callback`. Zoho's OAuth
 * redirect appends `code`, `state`, and (on multi-DC apps) `accounts-server`
 * — the latter is the DC accounts host the user actually authorized against.
 * We surface it so the code-exchange call can hit the right host.
 */
export const ZohoOAuthCallbackQuery = z
  .object({
    code: z.string().min(1),
    state: z.string().min(1),
    "accounts-server": z.string().optional(),
  })
  .strict();
export type ZohoOAuthCallbackQuery = z.infer<typeof ZohoOAuthCallbackQuery>;

/**
 * Response from `GET /accounts/me/zoho/connections/callback`. The connection
 * row has been persisted and verified inline; `status` reflects the inline
 * verify, `error` is non-null when the verify failed (the row is still saved
 * so the operator can rename / re-verify later).
 */
export const ZohoOAuthCallbackResult = z
  .object({
    id: z.string(),
    status: ZohoVerifyStatusSchema,
    error: z.string().nullable(),
    // Transiently `null` when the post-exchange `GET /organizations` probe
    // hasn't backfilled this yet; mirrors the nullable on `ZohoConnectionDto`.
    organizationName: z.string().nullable(),
    isDefault: z.boolean(),
  })
  .strict();
export type ZohoOAuthCallbackResult = z.infer<typeof ZohoOAuthCallbackResult>;

/**
 * Body for `PATCH /accounts/me/zoho/connections/:id` — only the display name
 * is editable through the UI. DC region, organization id, and the underlying
 * token material are immutable once connected; to change any of those the
 * operator deletes the row and reconnects.
 */
export const ZohoConnectionPatchDto = z
  .object({
    displayName: z.string().trim().min(1).max(120),
  })
  .strict();
export type ZohoConnectionPatchDto = z.infer<typeof ZohoConnectionPatchDto>;

/**
 * Response from `POST /accounts/me/zoho/connections/:id/verify`. Mirrors the
 * health columns on the connection row so the UI can refresh in place after
 * a manual re-verify without a second GET.
 */
export const ZohoVerifyResult = z
  .object({
    status: ZohoVerifyStatusSchema,
    error: z.string().nullable(),
    lastVerifiedAt: z.string().nullable(),
  })
  .strict();
export type ZohoVerifyResult = z.infer<typeof ZohoVerifyResult>;

/**
 * 409 envelope returned by `DELETE /accounts/me/zoho/connections/:id` when
 * the connection is still referenced by Wallet / WalletToken rows. The
 * operator must rehome those entities (point them at a different connection
 * or null) before the delete is allowed. `dependents` is bounded server-side
 * so a runaway tenant can't dump a 10k-row payload through the wire.
 */
export const ZohoDeleteBlockedResponse = z
  .object({
    statusCode: z.number(),
    code: z.literal("ZOHO_CONNECTION_IN_USE"),
    message: z.string(),
    dependents: z
      .object({
        wallets: z.array(
          z.object({ id: z.string(), name: z.string() }).strict(),
        ),
        walletTokens: z.array(
          z.object({ id: z.string(), name: z.string() }).strict(),
        ),
      })
      .strict(),
  })
  .strict();
export type ZohoDeleteBlockedResponse = z.infer<
  typeof ZohoDeleteBlockedResponse
>;

// ─── Phase 2: Sync target wiring ────────────────────────────────────────────
//
// A Wallet (folder) or WalletToken (address) can pin a specific
// `ZohoConnection` so the sync routes know which Zoho org to push into.
// Resolution at sync time walks: WalletToken pin → nearest Wallet ancestor
// pin → Account default. `ZohoTargetSource` carries the answer to that
// resolution so the UI can render "Inherits from folder X" inline.

export const ZohoTargetSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("walletToken") }).strict(),
  z
    .object({
      kind: z.literal("folder"),
      folderId: z.string(),
      folderName: z.string(),
    })
    .strict(),
  z.object({ kind: z.literal("account_default") }).strict(),
  z.object({ kind: z.literal("none") }).strict(),
]);
export type ZohoTargetSource = z.infer<typeof ZohoTargetSourceSchema>;

/**
 * Body of `PUT /api/v1/wallets/:id/zoho-target` and
 * `PUT /api/v1/wallet-tokens/:id/zoho-target`. `connectionId: null` clears
 * the pin (the resolver then falls through to the next inheritance step).
 */
export const ZohoSetTargetDto = z
  .object({ connectionId: z.string().min(1).nullable() })
  .strict();
export type ZohoSetTargetDto = z.infer<typeof ZohoSetTargetDto>;

/**
 * Response from `GET /api/v1/wallet-tokens/:id/zoho-target/resolved`. The
 * `connection` is null when nothing is resolvable (kind: "none"); otherwise
 * it is the full DTO so the UI can render display name + health pill
 * without a follow-up fetch.
 */
export const ZohoResolvedTargetResponse = z
  .object({
    connection: ZohoConnectionDto.nullable(),
    source: ZohoTargetSourceSchema,
  })
  .strict();
export type ZohoResolvedTargetResponse = z.infer<
  typeof ZohoResolvedTargetResponse
>;

/**
 * Response from the target PUT endpoints. Echoes the row's new
 * `zohoConnectionId` (null when cleared) so the UI can confirm the write
 * landed before refetching the resolved target.
 */
export const ZohoTargetUpdateResponse = z
  .object({
    id: z.string(),
    zohoConnectionId: z.string().nullable(),
  })
  .strict();
export type ZohoTargetUpdateResponse = z.infer<
  typeof ZohoTargetUpdateResponse
>;

export const ZohoSyncSummary = z
  .object({
    status: z.enum(["ok", "error"]),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    currenciesCreated: z.number().int().nonnegative(),
    accountsCreated: z.number().int().nonnegative(),
    txCreated: z.number().int().nonnegative(),
    txSkippedAlreadySynced: z.number().int().nonnegative(),
    txSkippedFiltered: z.number().int().nonnegative(),
    balancesProcessed: z.number().int().nonnegative(),
  })
  .strict();
export type ZohoSyncSummary = z.infer<typeof ZohoSyncSummary>;

/**
 * 202 Accepted envelope returned by `POST /wallet-tokens/:id/zoho/sync`.
 *
 * The inline-run path is gone — every enqueue writes a ZohoSyncJob row
 * first and returns immediately. `jobId` is always present. `summary` is
 * always null on enqueue; poll `GET /wallet-tokens/:id/zoho/sync/:jobId`
 * until status is 'ok' or 'error' to retrieve the final summary.
 */
export const ZohoSyncResponse = z
  .object({
    jobId: z.string(),
    summary: ZohoSyncSummary.nullable(),
    /**
     * Names of the Zoho connection that was synced into — surfaced so the
     * UI's confirm-success toast can name the target without a refetch.
     */
    zohoConnection: z
      .object({
        id: z.string(),
        displayName: z.string(),
        organizationName: z.string(),
      })
      .strict(),
  })
  .strict();
export type ZohoSyncResponse = z.infer<typeof ZohoSyncResponse>;

/**
 * Response shape for `GET /wallet-tokens/:id/zoho/sync/:jobId`.
 *
 * Status values and which fields are non-null in each state:
 *   'queued'  — job is waiting for the worker to pick it up.
 *               startedAt/finishedAt/heartbeatAt/summary are null.
 *   'running' — worker has started; heartbeatAt is non-null and updates
 *               every ~30 s. finishedAt/summary are still null.
 *   'ok'      — terminal success. finishedAt and summary are non-null.
 *               nextRetryAt is null (cleared on success).
 *   'error'   — terminal or retriable failure. errorCode/errorMessage
 *               are non-null. nextRetryAt is non-null when
 *               attemptsMade < maxAttempts and the error is transient.
 */
export const ZohoSyncStatusResponse = z
  .object({
    jobId: z.string(),
    walletTokenId: z.string(),
    zohoConnectionId: z.string(),
    status: z.enum(["queued", "running", "ok", "error"]),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    heartbeatAt: z.string().datetime().nullable(),
    summary: ZohoSyncSummary.nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    attemptsMade: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    nextRetryAt: z.string().datetime().nullable(),
  })
  .strict();
export type ZohoSyncStatusResponse = z.infer<typeof ZohoSyncStatusResponse>;

// ─── Zoho OAuth App config (super-admin) ────────────────────────────────────
//
// One row, app-wide. Holds the Zoho APP credentials (clientId / secret /
// redirectUri) the Phase 1 OAuth flow uses to identify us to Zoho. Was
// env-only; moved to DB so the super-admin can rotate from the UI.
//
// The encrypted secret NEVER crosses the wire — the GET response returns
// `clientSecretLastSet` (the row's updatedAt) so the UI can render
// "Set on 2026-05-31 by haim@…" without exposing the secret itself.

export const ZohoOAuthAppConfigDto = z
  .object({
    clientId: z.string(),
    redirectUri: z.string().url(),
    /** ISO timestamp the secret was last written. */
    clientSecretLastSet: z.string(),
    updatedByUserId: z.string(),
  })
  .strict();
export type ZohoOAuthAppConfigDto = z.infer<typeof ZohoOAuthAppConfigDto>;

export const ZohoOAuthAppConfigGetResponse = z
  .object({
    /** `null` when the super-admin has not yet written the credentials. */
    config: ZohoOAuthAppConfigDto.nullable(),
  })
  .strict();
export type ZohoOAuthAppConfigGetResponse = z.infer<
  typeof ZohoOAuthAppConfigGetResponse
>;

export const ZohoOAuthAppConfigWiringStatusSchema = z.enum([
  "uninitialized",
  "stale",
]);
export type ZohoOAuthAppConfigWiringStatus = z.infer<
  typeof ZohoOAuthAppConfigWiringStatusSchema
>;

export const ZohoOAuthAppConfigPutResponse = z
  .object({
    config: ZohoOAuthAppConfigDto,
    restartRequired: z.literal(true),
    /**
     * `uninitialized` — first-ever save; the Zoho connect/sync routes
     * are not yet registered in this process. Restart to wire them.
     * `stale` — a rotation; the routes are wired but using the OLD
     * credentials until restart.
     */
    wiringStatus: ZohoOAuthAppConfigWiringStatusSchema,
  })
  .strict();
export type ZohoOAuthAppConfigPutResponse = z.infer<
  typeof ZohoOAuthAppConfigPutResponse
>;

// No whitespace / control chars in clientId. Defends against the silent
// copy/paste mistake where a trailing newline corrupts the OAuth call
// with an opaque "invalid_client" much later. We allow `.` `-` `_`
// because legitimate Zoho IDs (`1000.XXXXX...`) carry the dot.
const ZOHO_CLIENT_ID_SHAPE = /^[^\s\x00-\x1f\x7f]+$/;

// redirectUri is echoed back to the browser at the end of the OAuth
// dance — `data:`, `javascript:`, `file:`, `vbscript:` etc. are all XSS
// vectors even if the route is super-admin-only (compromised super-admin
// or honest mistake). `z.string().url()` accepts every well-formed URI;
// this regex narrows to http(s) only.
const HTTP_S_SCHEME = /^https?:\/\//i;

export const ZohoOAuthAppConfigUpsertDto = z
  .object({
    clientId: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(
        ZOHO_CLIENT_ID_SHAPE,
        "clientId must not contain whitespace or control characters",
      ),
    /**
     * Plaintext on the wire — TLS + super-admin-only route. The server
     * encrypts at rest using AES-256-GCM keyed by env ZOHO_ENCRYPTION_KEY.
     */
    clientSecret: z.string().trim().min(1).max(500),
    redirectUri: z
      .string()
      .url()
      .max(500)
      .regex(
        HTTP_S_SCHEME,
        "redirectUri must use http or https — data:, javascript:, file: etc. are rejected",
      ),
  })
  .strict();
export type ZohoOAuthAppConfigUpsertDto = z.infer<
  typeof ZohoOAuthAppConfigUpsertDto
>;
